import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { cabinet } from '../../api/cabinet';
import { isApiError } from '../../api/client';
import type { PublicFeedback } from '../../api/types';
import { useSession } from '../../app/session';
import { ErrorBlock } from '../../components/ErrorBlock';
import { Loading } from '../../components/Loading';
import { useI18n, useT } from '../../i18n';
import { FeedbackForm } from './FeedbackForm';

export type FeedbackView = 'loading' | 'open' | 'answered' | 'expired' | 'opted_out' | 'not_found' | 'error';

/** API error of GET/POST /public/feedback/{token} → what to show. */
export function viewForError(e: unknown): FeedbackView {
  if (!isApiError(e)) return 'error';
  if (e.status === 404) return 'not_found';
  if (e.status === 410 || e.code === 'expired') return 'expired';
  if (e.status === 409 || e.code === 'already_answered') return 'answered';
  return 'error';
}

export function viewForStatus(status: string | null | undefined): FeedbackView {
  if (status === 'open' || status === 'answered' || status === 'expired' || status === 'opted_out') return status;
  return 'not_found';
}

/**
 * Public page from the feedback message (`/feedback/:token`, no login): «Як ми впоралися?» with five ratings, an
 * optional text and «Більше не надсилати мені таких запитів». Calm copy in every state.
 */
export function FeedbackPage() {
  const { token = '' } = useParams();
  const t = useT();
  const authed = useSession((s) => s.status === 'authed');
  const [view, setView] = useState<FeedbackView>('loading');
  const [info, setInfo] = useState<PublicFeedback | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [optedOutNow, setOptedOutNow] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setView('loading');
    cabinet
      .publicFeedback(token)
      .then((r) => {
        if (!alive) return;
        setInfo(r);
        setView(viewForStatus(r?.status));
        // мова листа (мова контакту), якщо людина ще не обрала мову і не увійшла
        if (r?.locale && !useI18n.getState().chosen && !useI18n.getState().contact) useI18n.getState().setContactLocale(r.locale);
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setError(e);
        setView(viewForError(e));
      });
    return () => {
      alive = false;
    };
  }, [token, attempt]);

  const heading = (
    <>
      {info?.brand ? <p className="eyebrow">{info.brand}</p> : null}
      <h1 className="mt-3 text-[28px] leading-tight sm:text-[34px]">{t('feedback.title')}</h1>
      {info?.case_number != null ? <p className="mt-2 text-[14px] text-lab">{t('cases.number', { n: info.case_number })}</p> : null}
    </>
  );
  const note = (key: Parameters<typeof t>[0]) => (
    <p role="status" className="mt-6 max-w-prose text-[16px] leading-relaxed text-txt">
      {t(key)}
    </p>
  );
  const toCabinet = (
    <Link to={authed ? '/cases' : '/login'} className="link mt-6">
      {t('feedback.toCabinet')}
    </Link>
  );

  if (view === 'loading') return <Loading />;
  if (view === 'error') return <ErrorBlock error={error} onRetry={() => setAttempt((n) => n + 1)} />;
  if (view === 'not_found')
    return (
      <>
        <h1 className="text-[28px] leading-tight sm:text-[34px]">{t('feedback.title')}</h1>
        {note('feedback.notFound')}
        {toCabinet}
      </>
    );

  return (
    <>
      {heading}
      {view === 'open' ? (
        <>
          <p className="mt-4 max-w-prose text-[16px] leading-relaxed text-txt">{t('feedback.intro')}</p>
          <FeedbackForm
            showOptOut
            onSubmit={async (v) => {
              try {
                await cabinet.answerPublicFeedback(token, v);
                setOptedOutNow(!!v.opt_out);
                setView('answered');
              } catch (e) {
                const next = viewForError(e);
                if (next === 'error') throw e;
                setView(next);
              }
            }}
          />
        </>
      ) : null}
      {view === 'answered' ? (
        <>
          {note('feedback.thanks')}
          {optedOutNow ? <p className="mt-3 text-[15px] text-txt">{t('feedback.optedOutDone')}</p> : null}
        </>
      ) : null}
      {view === 'expired' ? (
        <>
          {note('feedback.expired')}
          {toCabinet}
        </>
      ) : null}
      {view === 'opted_out' ? note('feedback.optedOut') : null}
    </>
  );
}
